// ─────────────────────────────────────────────
//  ProfileLogic.js  —  API-driven Profile Logic
// ─────────────────────────────────────────────

import { useState, useRef, useEffect } from "react";
import { useAuth } from "../../../context/AuthContext";
import { getDocuments } from "./Auth.js";
import ApplicationService from "../../../services/applicationService";
import WalletService from "../../../services/walletService";
import UserService from "../../../services/userService";
import RedemptionService from "../../../services/redemptionService";


export const SVC_GST_RATE = 0.18;

export function splitSvcFeeGST(svcFeeInclusive) {
  const base = Math.round(svcFeeInclusive / (1 + SVC_GST_RATE));
  const gst  = svcFeeInclusive - base;
  return { base, gst };
}

export function commissionableBase(svcFeeInclusive) {
  return splitSvcFeeGST(svcFeeInclusive).base;
}

// ════════════════════════════════════════════════
//  UI CONSTANTS
// ════════════════════════════════════════════════
export const VISIBLE_COUNT = 4;

// ════════════════════════════════════════════════
//  APPLICATION LIMIT CONSTANTS
// ════════════════════════════════════════════════
export const MAX_APPLICATIONS = 2500;
export const INDIVIDUAL_LIMIT = 5;

export const APPLICATION_STATUS = {
  PENDING:   "pending",
  COMPLETED: "completed",
};

export const DECISION_STATUS_LABELS = {
  approved: { label: "Approved", color: "#16a34a", icon: "✓" },
  declined: { label: "Declined", color: "#dc2626", icon: "✕" },
};

export const TAB_LABELS = {
  PENDING:   "Pending",
  COMPLETED: "Completed",
};

// ── Document Requirements Per Role ───────────
export const DOCUMENT_REQUIREMENTS = {
  individual: [
    { key: "passport", label: "Passport",     icon: "📄", required: true  },
    { key: "aadhaar",  label: "Aadhaar Card", icon: "📋", required: true  },
    { key: "pan",      label: "PAN Card",      icon: "💳", required: true  },
    { key: "void_cheque", label: "VoidCheque",  icon: "💳", required: true  },
  ],
  member: [
    { key: "passport", label: "Passport",     icon: "📄", required: true  },
    { key: "aadhaar",  label: "Aadhaar Card", icon: "📋", required: true  },
    { key: "pan",      label: "PAN Card",      icon: "💳", required: true  },
    { key: "void_cheque", label: "VoidCheque",  icon: "💳", required: true  },
  ],
  agent: [
    { key: "gst",     label: "GST Certificate",          icon: "📜", required: true  },
    { key: "pan",     label: "PAN Card",                  icon: "💳", required: true  },
    { key: "aadhaar", label: "Aadhaar Card",              icon: "📋", required: true  },
    { key: "address", label: "Office Address Proof",      icon: "🏢", required: true  },
    { key: "bank",    label: "Bank Statement (6 months)", icon: "🏦", required: false },
    { key: "void_cheque", label: "VoidCheque",             icon: "💳", required: true  },
    { key: "gst_invoice", label: "GST-Invoice",            icon: "📜", required: true  },
  ],
};

export const ROLE_CONFIG = {
  individual: { label: "Individual", color: "#1d4ed8", icon: "👤" },
  member:     { label: "Member",     color: "#7c3aed", icon: "👥" },
  agent:      { label: "Agent",      color: "#d97706", icon: "🏢" },
};

// ── Commission rates ──
export const COMMISSION_RATES = {
  individual: 0.05,
  member:     0.10,
  agent:      0.20,
};

export const COMMISSION_LABELS = {
  individual: "5%",
  member:     "10%",
  agent:      "20%",
};

export const REFERRAL_DISCOUNT_RATES = {
  individual: 0.05,
  member:     0.10,
  agent:      0.20,
};

export const MILESTONE_REWARD_RATES = {
  individual: 0.05,
  member:     0.10,
  agent:      0.20,
};

export const MILESTONES = [
  { id: "milestone_50k",  spendTarget: 50000,  pointReward: 2500,  label: "Silver Milestone",  icon: "🥈" },
  { id: "milestone_1L",   spendTarget: 100000, pointReward: 10000, label: "Gold Milestone",    icon: "🥇" },
];

export const WELCOME_BONUS          = 100;
export const MIN_SVC_FEE_REDEMPTION = 299;
export const REWARD_DISCOUNT_MAX_RATE = 0.20;
export const PROTECT_FEE            = 299;
export const PROTECT_FEE_FIRST     = 1;
export const PROTECT_FEE_REPEAT   = 299;

export const GENDER_OPTIONS = [
  "Male", "Female", "Non-Binary", "Genderqueer", "Genderfluid",
  "Agender", "Bigender", "Two-Spirit", "Prefer not to say", "Other",
];

export const MARITAL_STATUS_OPTIONS = [
  "Single", "Married", "Divorced", "Widowed", "Separated",
  "Domestic Partnership", "Civil Union", "Prefer not to say",
];

export const COUNTRY_CODES = [
  { code: "+91",  country: "IN", flag: "🇮🇳" },
  { code: "+1",   country: "US", flag: "🇺🇸" },
  { code: "+44",  country: "GB", flag: "🇬🇧" },
  { code: "+971", country: "AE", flag: "🇦🇪" },
  { code: "+65",  country: "SG", flag: "🇸🇬" },
  { code: "+60",  country: "MY", flag: "🇲🇾" },
  { code: "+61",  country: "AU", flag: "🇦🇺" },
  { code: "+49",  country: "DE", flag: "🇩🇪" },
  { code: "+33",  country: "FR", flag: "🇫🇷" },
  { code: "+81",  country: "JP", flag: "🇯🇵" },
  { code: "+86",  country: "CN", flag: "🇨🇳" },
  { code: "+7",   country: "RU", flag: "🇷🇺" },
  { code: "+55",  country: "BR", flag: "🇧🇷" },
  { code: "+27",  country: "ZA", flag: "🇿🇦" },
  { code: "+92",  country: "PK", flag: "🇵🇰" },
  { code: "+880", country: "BD", flag: "🇧🇩" },
  { code: "+94",  country: "LK", flag: "🇱🇰" },
  { code: "+977", country: "NP", flag: "🇳🇵" },
  { code: "+966", country: "SA", flag: "🇸🇦" },
  { code: "+974", country: "QA", flag: "🇶🇦" },
];

// ═══════════════════════════════════════════════════════════
//  MEMBER HELP CONTACT CONSTANTS
// ═══════════════════════════════════════════════════════════
export const MEMBER_HELP_CONTACT = {
  SUPPORT_NUMBER: "+91 9810044015",
  SUPPORT_EMAIL: "ihnexperience@gmail.com",
  WHATSAPP_NUMBER: "+91 9810044015",
};

export const MEMBER_HELP_TEXT = {
  TITLE: "Need Help?",
  SUBTITLE: "Contact your dedicated member support",
  PHONE_LABEL: "Phone",
  EMAIL_LABEL: "Email",
  WHATSAPP_LABEL: "WhatsApp",
  WHATSAPP_PRETEXT: "Hi TheVisaCo, I need help with my visa application. Here's my details:",
  HELP_NOTE: "Mon-Sat, 9:30 AM - 5:30 PM IST",
  COPY_SUCCESS: "Copied!",
};

export const REDEMPTION_STATUS = {
  PENDING:   "pending",
  COMPLETED: "completed",
  REJECTED:  "rejected",
};

// ════════════════════════════════════════════════
//  API-BASED DATA FUNCTIONS
// ════════════════════════════════════════════════

export async function fetchProfileFromApi() {
  try {
    return await UserService.getProfile();
  } catch (err) { console.error("[API] fetchProfileFromApi failed:", err); return null; }
}

// ── Applications ────────────────────────────
export async function getApplications(userEmail) {
  if (!userEmail) return [];
  try {
    const result = await ApplicationService.list();
    return (result.data || []).map((a) => ({
      ...a,
      applicationId: a.humanId || String(a.id || ""),
    }));
  } catch (err) {
    console.error("[API] getApplications failed:", err);
    return [];
  }
}

export async function saveApplication(userEmail, visaId, data) {
  try {
    const payload = {
      visaSlug: visaId,
      isExpress: data?.expressVisa || false,
      applicants: [],
    };
    const result = await ApplicationService.create(payload);
    return result;
  } catch (err) {
    console.error("[API] saveApplication failed:", err);
    throw err;
  }
}

export async function deleteApplication(userEmail, applicationId) {
  try {
    await ApplicationService.cancel(applicationId);
    return [];
  } catch (err) {
    console.error("[API] deleteApplication failed:", err);
    return [];
  }
}

// ── Wallet ──────────────────────────────────
export async function getWallet(userEmail) {
  if (!userEmail) return _defaultWallet();
  try {
    return await WalletService.getWallet();
  } catch (err) {
    console.error("[API] getWallet failed:", err);
    return _defaultWallet();
  }
}

export async function saveWallet(userEmail, wallet) {
  return wallet;
}

// ── API-backed fetch functions ──────────────
export async function fetchRedemptionRequests(userEmail) {
  if (!userEmail) return [];
  try {
    const result = await RedemptionService.listRedemptions();
    return (result.data || []).map(r => ({
      id: r.human_id || r.id,
      offerName: r.label || 'Redemption',
      amount: r.amount || 0,
      status: r.status || 'pending',
      submittedAt: r.created_at,
      updatedAt: r.updated_at,
    }));
  } catch (err) {
    console.error("[API] fetchRedemptionRequests failed:", err);
    return [];
  }
}

export async function fetchPayoutRequests(userEmail) {
  if (!userEmail) return [];
  try {
    const result = await RedemptionService.listPayouts();
    return (result.data || []).map(r => ({
      id: r.human_id || r.id,
      amount: r.amount || 0,
      status: r.status || 'pending',
      bankDetails: r.bank_details || {},
      submittedAt: r.created_at,
      updatedAt: r.updated_at,
    }));
  } catch (err) {
    console.error("[API] fetchPayoutRequests failed:", err);
    return [];
  }
}

export async function deletePayoutRequest(userEmail, payoutId) {
  if (!userEmail || !payoutId) return [];
  try {
    await RedemptionService.requestPayout(0); // no-op to hit API — actual delete TBD
  } catch (err) { console.error("[API] deletePayoutRequest failed:", err); }
  return fetchPayoutRequests(userEmail);
}

export async function fetchVoucherRedemptions(userEmail) {
  if (!userEmail) return [];
  try {
    const result = await RedemptionService.listVouchers();
    return (result.data || []).map(v => ({
      id: `v_${v.id}`,
      voucherId: v.id,
      brand: v.brand || '',
      category: v.category || '',
      discount: v.discount || '',
      status: 'completed',
      submittedAt: v.created_at || new Date().toISOString(),
    }));
  } catch (err) {
    console.error("[API] fetchVoucherRedemptions failed:", err);
    return [];
  }
}

export async function fetchMilestoneData(userEmail) {
  if (!userEmail) return _defaultMilestoneData();
  try {
    const data = await WalletService.getMilestone();
    return {
      totalSvcFeeSpent: data.total_svc_fee_spent || 0,
      achievedMilestones: data.achieved_milestones || [],
      startedAt: data.started_at || new Date().toISOString(),
    };
  } catch (err) {
    console.error("[API] fetchMilestoneData failed:", err);
    return _defaultMilestoneData();
  }
}

export async function fetchReferralCode(userEmail) {
  if (!userEmail) return null;
  try {
    const data = await UserService.getReferralCode();
    return data.referralCode;
  } catch (err) {
    console.error("[API] fetchReferralCode failed:", err);
    return null;
  }
}

// ── Milestone Spend Tracking (API-driven) ───
function _defaultMilestoneData() {
  return { totalSvcFeeSpent: 0, achievedMilestones: [], startedAt: new Date().toISOString() };
}

export async function processMilestoneRewards(userEmail, role, svcFee, country) {
  if (!userEmail || svcFee <= 0) return _defaultMilestoneData();
  try {
    const data = await WalletService.getMilestone();
    return {
      totalSvcFeeSpent: data.total_svc_fee_spent || 0,
      achievedMilestones: data.achieved_milestones || [],
      startedAt: data.started_at || new Date().toISOString(),
    };
  } catch (err) {
    console.error("[API] processMilestoneRewards failed:", err);
    return _defaultMilestoneData();
  }
}

// ── Referral helpers (API-driven) ───────────
export async function lookupReferralOwner(code) {
  if (!code) return null;
  try {
    const data = await UserService.lookupReferral(code);
    return data.email || null;
  } catch (err) {
    console.error("[API] lookupReferralOwner failed:", err);
    return null;
  }
}

export async function assignReferralCode(userEmail, userId, role) {
  if (!userEmail || !role || role === "individual") return null;
  try {
    const data = await UserService.getReferralCode();
    return data.referralCode;
  } catch (err) {
    console.error("[API] assignReferralCode failed:", err);
    return null;
  }
}

// ── Wallet reward operations (API-driven) ───
export async function creditCommission(userEmail, role, svcFee, country) {
  if (!userEmail) return _defaultWallet();
  try {
    return await WalletService.getWallet();
  } catch (err) {
    console.error("[API] creditCommission failed:", err);
    return _defaultWallet();
  }
}

export async function redeemNonRedeemablePoints(userEmail, amount, country) {
  if (!userEmail) return { success: false, wallet: _defaultWallet() };
  try {
    const wallet = await WalletService.getWallet();
    return { success: true, wallet };
  } catch (err) {
    console.error("[API] redeemNonRedeemablePoints failed:", err);
    return { success: false, wallet: _defaultWallet() };
  }
}

export async function spendRewardMoney(userEmail, amount, description) {
  if (!userEmail) return { success: false, wallet: _defaultWallet() };
  try {
    const wallet = await WalletService.getWallet();
    return { success: true, wallet };
  } catch (err) {
    console.error("[API] spendRewardMoney failed:", err);
    return { success: false, wallet: _defaultWallet() };
  }
}

// ── Document file data (API-backed) ──────────
export async function getDocFileData(userEmail, docKey) {
  if (!userEmail || !docKey) return null;
  try {
    const docs = await getDocuments();
    let doc;
    if (Array.isArray(docs)) {
      doc = docs.find((d) => (d.docKey || d.doc_key) === docKey);
    } else if (docs && typeof docs === "object") {
      doc = docs[docKey];
    }
    return doc?.file_url || doc?.url || null;
  } catch (err) {
    console.error("[API] getDocFileData failed:", err);
    return null;
  }
}

// ── Misc Utilities ────────────────────────────
export function convertImageToPdfBlob(imageDataUrl) {
  const base64Data     = imageDataUrl.split(",")[1];
  const byteCharacters = atob(base64Data);
  const byteNumbers    = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
  return new Blob([new Uint8Array(byteNumbers)], { type: "image/jpeg" });
}

export function formatFileSize(bytes) {
  if (!bytes)          return "—";
  if (bytes < 1024)    return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(2)} MB`;
}

export function buildFileInfo(file) {
  return { name: file.name, size: formatFileSize(file.size), type: file.type, url: URL.createObjectURL(file) };
}

export function formatDate(isoString) {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export function formatCurrency(amount) {
  return `₹${Math.abs(amount).toLocaleString("en-IN")}`;
}

export function emptyApplicant() {
  return {
    firstName: "", lastName: "", dob: "", gender: "", maritalStatus: "",
    passportNumber: "", passportValidTill: "", placeOfIssue: "",
    email: "", phoneCode: "+91", phone: "", occupation: "", agencyName: "",
    passportFront: null, passportBack: null, bankStatement: null,
    visaPhoto: null, biometric: null, flightTicket: null,
    travelInsurance: null, invitationLetter: null, otherDoc: null, otherDoc2: null,
    flight:  { arrival: "", departure: "", stops: "", flightNumber: "", arrivalDate: "", departureDate: "" },
    transit: { arrival: "", departure: "", stops: "", flightNumber: "", arrivalDate: "", departureDate: "" },
  };
}

// ════════════════════════════════════════════════
//  SEND EMAIL HELPER (mailto simulation)
// ════════════════════════════════════════════════
export const ADMIN_EMAIL = "visacob2b@gmail.com";

export function sendAdminEmail(subject, body) {
  console.log(`[EMAIL TO ${ADMIN_EMAIL}]`, subject, body);
}

// ════════════════════════════════════════════════
//  useProfileState
// ════════════════════════════════════════════════
export function useProfileState(onLogout) {
  const { user: authUser, logout: authLogout } = useAuth();

  const [user, setUser] = useState(authUser);
  const [showWelcome, setShowWelcome] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [uploading, setUploading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraDocKey, setCameraDocKey] = useState(null);
  const [docFiles, setDocFiles] = useState({});
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [editName, setEditName] = useState(authUser?.name || "");
  const [editPhone, setEditPhone] = useState(authUser?.phone || "");
  const [editCity, setEditCity] = useState(authUser?.city || "");
  const [applications, setApplications] = useState([]);
  const [wallet, setWallet] = useState(_defaultWallet());
  const [referralCode, setReferralCode] = useState(null);
  const [milestoneData, setMilestoneData] = useState(_defaultMilestoneData());
  const [paymentModal, setPaymentModal] = useState(null);
  const [payStep, setPayStep] = useState(1);
  const [applicantList, setApplicantList] = useState([]);
  const [appCountInput, setAppCountInput] = useState(1);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [draftMap, setDraftMap] = useState({});
  const [referralInput, setReferralInput] = useState("");
  const [referralError, setReferralError] = useState("");
  const [referralApplied, setReferralApplied] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [useWalletOffer, setUseWalletOffer] = useState(false);
  const [walletOfferError, setWalletOfferError] = useState("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(PAYMENT_METHOD_KEYS.DEBIT_VISA);
  const [payProcessing, setPayProcessing] = useState(false);
  const [redemptionRequests, setRedemptionRequests] = useState([]);
  const [voucherRedemptions, setVoucherRedemptions] = useState([]);
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [hasPaid, setHasPaid] = useState(false);
  const [welcomeBonusCredited, setWelcomeBonusCredited] = useState(false);

  const videoRef  = useRef(null);
  const streamRef = useRef(null);
  const lastAppIdRef = useRef(null);
  const draftsRef = useRef({});

  const docRequirements = DOCUMENT_REQUIREMENTS[user?.role] || DOCUMENT_REQUIREMENTS.individual;
  const roleConfig      = ROLE_CONFIG[user?.role]            || ROLE_CONFIG.individual;
  const commissionRate  = COMMISSION_RATES[user?.role]       || 0;
  const commissionPct   = COMMISSION_LABELS[user?.role]      || COMMISSION_LABELS.individual;

  const pendingApplications    = applications.filter((a) => a.status === APPLICATION_STATUS.PENDING);
  const completedApplications  = applications.filter((a) => a.status === APPLICATION_STATUS.COMPLETED);
  const processingApplications = completedApplications.filter((a) => !a.decisionStatus);
  const decisionApplications   = completedApplications.filter((a) => !!a.decisionStatus);

  // Keep local user in sync when authUser arrives/changes asynchronously
  useEffect(() => {
    if (authUser) {
      setUser((prev) => ({ ...prev, ...authUser }));
    }
  }, [authUser]);

  // Load data from API on mount
  useEffect(() => {
    if (!authUser?.email) return;

    UserService.getProfile().then((profile) => {
      if (!profile) return;
      setUser((prev) => ({ ...prev, ...profile, authProvider: prev.authProvider || profile.authProvider, hasPassword: prev.hasPassword !== undefined ? prev.hasPassword : profile.hasPassword }));
    }).catch(() => {});

    getApplications(authUser.email).then((apps) => {
      setApplications(apps);
    });

    getWallet(authUser.email).then((w) => setWallet(w));

    fetchReferralCode(authUser.email).then((code) => setReferralCode(code));
    fetchMilestoneData(authUser.email).then((data) => setMilestoneData(data));
    fetchRedemptionRequests(authUser.email).then((list) => setRedemptionRequests(list));
    fetchVoucherRedemptions(authUser.email).then((list) => setVoucherRedemptions(list));
    fetchPayoutRequests(authUser.email).then((list) => setPayoutRequests(list));

    getDocuments().then((docs) => {
      const docMap = {};
      if (Array.isArray(docs)) {
        docs.forEach((d) => {
          const key = d.docKey || d.doc_key;
          if (key) docMap[key] = d;
        });
      } else if (docs && typeof docs === 'object') {
        Object.assign(docMap, docs);
      }
      if (Object.keys(docMap).length > 0) setUploadedDocs(docMap);
    });

    setShowWelcome(false);
  }, [authUser?.email]);

  function dismissWelcome(goToDocuments = false) {
    setShowWelcome(false);
    if (goToDocuments) setActiveTab("documents");
  }

  function handleFilePick(docKey, file) {
    if (!file) return;
    setDocFiles((prev) => ({ ...prev, [docKey]: { file, info: buildFileInfo(file) } }));
  }

  function handleApplicantFilePick(index, field, file) {
    if (!file) return;
    setApplicantList(prev => {
      const updated = [...prev];
      if (updated[index]) updated[index] = { ...updated[index], [field]: { file, info: buildFileInfo(file) } };
      if (payStep >= 2 && paymentModal) {
        _saveDraft(paymentModal.applicationId, { applicantList: updated, appCountInput, payStep });
        setDraftMap((dm) => ({ ...dm, [paymentModal.applicationId]: true }));
      }
      return updated;
    });
  }

  function _saveDraft(appId, data) {
    if (!appId) return;
    draftsRef.current[`${user?.email}_${appId}`] = {
      appCountInput: data.appCountInput,
      payStep: data.payStep,
      applicantList: (data.applicantList || []).map((a) => ({
        ...a,
        passportFront: a.passportFront?.info ? { _name: a.passportFront.info.name } : null,
        passportBack: a.passportBack?.info ? { _name: a.passportBack.info.name } : null,
        bankStatement: a.bankStatement?.info ? { _name: a.bankStatement.info.name } : null,
        visaPhoto: a.visaPhoto?.info ? { _name: a.visaPhoto.info.name } : null,
        biometric: a.biometric?.info ? { _name: a.biometric.info.name } : null,
        flightTicket: a.flightTicket?.info ? { _name: a.flightTicket.info.name } : null,
        travelInsurance: a.travelInsurance?.info ? { _name: a.travelInsurance.info.name } : null,
        invitationLetter: a.invitationLetter?.info ? { _name: a.invitationLetter.info.name } : null,
        otherDoc: a.otherDoc?.info ? { _name: a.otherDoc.info.name } : null,
        otherDoc2: a.otherDoc2?.info ? { _name: a.otherDoc2.info.name } : null,
      })),
    };
  }

  function _getDraft(appId) {
    if (!appId) return null;
    return draftsRef.current[`${user?.email}_${appId}`] || null;
  }

  function _clearDraft(appId) {
    if (!appId) return;
    delete draftsRef.current[`${user?.email}_${appId}`];
  }

  async function openCamera(docKey) {
    setCameraDocKey(docKey);
    setCameraActive(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch {
      alert("Camera access denied or unavailable.");
      setCameraActive(false);
    }
  }

  function capturePhoto() {
    if (!videoRef.current || !cameraDocKey) return;
    const canvas  = document.createElement("canvas");
    canvas.width  = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext("2d").drawImage(videoRef.current, 0, 0);
    const dataUrl    = canvas.toDataURL("image/jpeg", 0.92);
    const imageBlob  = convertImageToPdfBlob(dataUrl);
    const file       = new File([imageBlob], `${cameraDocKey}_photo.jpg`, { type: "image/jpeg" });
    const [_field, _idx] = cameraDocKey.split('_');
    if (_idx && !isNaN(parseInt(_idx))) {
      handleApplicantFilePick(parseInt(_idx), _field, file);
    } else {
      handleFilePick(cameraDocKey, file);
    }
    closeCamera();
  }

  function closeCamera() {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    setCameraActive(false);
    setCameraDocKey(null);
  }

  async function handleUploadDocuments() {
    if (!user?.email) { alert("User not found. Please login again."); return; }
    const hasNewFiles = Object.keys(docFiles).length > 0;
    if (!hasNewFiles) {
      setShowWelcome(false);
      setUser((prev) => ({ ...prev, profileComplete: true }));
      UserService.updateProfile({ profileComplete: true }).catch(() => {});
      return;
    }
    setUploading(true);
    await new Promise((r) => setTimeout(r, 1200));
    for (const [key, value] of Object.entries(docFiles)) {
      const { file } = value;
      if (!file) continue;
      try {
        await UserService.uploadDocument(key, file);
        setUploadedDocs((prev) => ({ ...prev, [key]: true }));
      } catch (err) {
        console.error("API upload failed for", key, err?.response?.data || err.message || err);
        setUploadedDocs((prev) => ({ ...prev, [key]: true }));
      }
    }
    setUploading(false);
    setSaveSuccess(true);
    setShowWelcome(false);
    setUser((prev) => ({ ...prev, profileComplete: true }));
    UserService.updateProfile({ profileComplete: true }).catch(() => {});

    if (!welcomeBonusCredited) {
      try {
        await WalletService.creditWelcomeBonus(user.email);
        setWelcomeBonusCredited(true);
        getWallet(user.email).then((w) => setWallet(w));
      } catch {
        console.warn("Welcome bonus API unavailable");
      }
    }

    setTimeout(() => {
      setSaveSuccess(false);
      alert(!welcomeBonusCredited
        ? `Documents uploaded successfully! 🎉 You've received a Welcome Bonus of ₹${WELCOME_BONUS}!`
        : "Documents uploaded successfully!");
    }, 1000);
  }

  async function handleSaveProfile() {
    setUploading(true);
    await new Promise((r) => setTimeout(r, 600));
    try {
      await UserService.updateProfile({ name: editName, phone: editPhone, city: editCity });
    } catch (err) { console.error("[API] handleSaveProfile failed:", err); }
    setUser((prev) => ({ ...prev, name: editName, phone: editPhone, city: editCity }));
    setUploading(false);
    setEditMode(false);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2500);
  }

  function openPaymentModal(app) {
    setPaymentModal(app);
    const sameApp = lastAppIdRef.current === app.applicationId;
    lastAppIdRef.current = app.applicationId;
    if (sameApp && applicantList.length > 0) {
      setPayStep(payStep >= 2 ? payStep : 1);
    } else {
      const draft = _getDraft(app.applicationId);
      if (draft && draft.applicantList?.length > 0) {
        setApplicantList(draft.applicantList);
        if (draft.appCountInput) setAppCountInput(draft.appCountInput);
        setPayStep(Math.max(draft.payStep || 2, 2));
      } else {
        setApplicantList([]);
        const isFirstTime = user?.role === 'member' && !hasPaid;
        setAppCountInput(isFirstTime ? 5 : 1);
        setPayStep(1);
      }
    }
    setReferralInput(""); setReferralError(""); setReferralApplied(false);
    setDiscountAmount(0); setUseWalletOffer(false); setWalletOfferError("");
    setSelectedPaymentMethod(PAYMENT_METHOD_KEYS.DEBIT_VISA);
  }

  function closePaymentModal() {
    if (payStep >= 1 && paymentModal) {
      _saveDraft(paymentModal.applicationId, { applicantList, appCountInput, payStep });
      setDraftMap((prev) => ({ ...prev, [paymentModal.applicationId]: true }));
    }
    setPaymentModal(null);
    setReferralApplied(false); setDiscountAmount(0);
    setReferralInput(""); setReferralError("");
    setShowUpgradeModal(false); setUseWalletOffer(false);
    setWalletOfferError(""); setSelectedPaymentMethod(PAYMENT_METHOD_KEYS.DEBIT_VISA);
  }

  function saveCurrentDraft() {
    if (payStep >= 1 && paymentModal) {
      _saveDraft(paymentModal.applicationId, { applicantList, appCountInput, payStep });
      setDraftMap((prev) => ({ ...prev, [paymentModal.applicationId]: true }));
    }
  }

  function handleApplicationCountChange(val) {
    const num = parseInt(val) || 0;
    if (num > MAX_APPLICATIONS) { alert(`Maximum ${MAX_APPLICATIONS} applications allowed.`); return; }
    setAppCountInput(num);
  }

  function confirmProceedWithCount() {
    const count = parseInt(appCountInput) || 0;
    if (count <= 0) { alert("Please enter at least 1 application."); return; }
    const isFirstTimeMember = user?.role === 'member' && !hasPaid;
    if (isFirstTimeMember && count < 5) {
      alert("As a new Member, you are required to apply for a minimum of 5 applications on your first order.");
      return;
    }
    if (user?.role === 'individual' && count > INDIVIDUAL_LIMIT) {
      setShowUpgradeModal(true);
    } else {
      generateApplicantForms(count);
    }
  }

  function handleUpgradeDecision(upgrade) {
    setShowUpgradeModal(false);
    if (upgrade) {
      const namePart = user.name.replace(/\s+/g, "").substring(0, 3).toUpperCase();
      const randomNum = Math.floor(1000 + Math.random() * 9000);
      const newUserId = `${namePart}M${randomNum}`;
      setUser(prev => ({ ...prev, role: 'member', userId: newUserId }));
    }
    generateApplicantForms(appCountInput);
  }

  function generateApplicantForms(count) {
    const newApplicants = [];
    for (let i = 0; i < count; i++) newApplicants.push(emptyApplicant());
    setApplicantList(newApplicants);
    setPayStep(2);
  }

  function updateApplicantField(index, field, value) {
    setApplicantList(prev => {
      const updated = [...prev];
      if (updated[index]) {
        if (field.includes(".")) {
          const [section, key] = field.split(".");
          updated[index] = { ...updated[index], [section]: { ...updated[index][section], [key]: value } };
        } else {
          updated[index][field] = value;
        }
      }
      if (payStep >= 2 && paymentModal) {
        _saveDraft(paymentModal.applicationId, { applicantList: updated, appCountInput, payStep });
        setDraftMap((dm) => ({ ...dm, [paymentModal.applicationId]: true }));
      }
      return updated;
    });
  }

  function removeApplicant(index) {
    setApplicantList(prev => {
      const updated = [...prev];
      updated.splice(index, 1);
      if (updated.length === 0 && paymentModal) {
        _saveDraft(paymentModal.applicationId, { applicantList: updated, appCountInput: Math.max(1, prev.length - 1), payStep: 1 });
        setDraftMap((dm) => ({ ...dm, [paymentModal.applicationId]: false }));
        setPayStep(1);
      }
      return updated;
    });
    setAppCountInput(prev => Math.max(1, prev - 1));
  }

  function addApplicant() {
    setApplicantList(prev => {
      if (prev.length >= MAX_APPLICATIONS) {
        alert(`Maximum ${MAX_APPLICATIONS} applications allowed.`);
        return prev;
      }
      return [...prev, emptyApplicant()];
    });
    setAppCountInput(prev => Math.min(prev + 1, MAX_APPLICATIONS));
  }

  async function applyReferralCode() {
    const code = referralInput.trim().toUpperCase();
    if (!code) { setReferralError("Enter a referral code."); return; }
    try {
      const ownerData = await lookupReferralOwner(code);
      if (!ownerData) { setReferralError("Invalid referral code."); return; }
      if (ownerData === user?.email) { setReferralError("You cannot use your own referral code."); return; }
      const discountRate = REFERRAL_DISCOUNT_RATES.member;
      const totalSvcFee  = (paymentModal?.svcFee || 0) * (applicantList.length || parseInt(appCountInput) || 1);
      const disc = Math.round(commissionableBase(totalSvcFee) * discountRate);
      setDiscountAmount(disc);
      setReferralApplied(true);
      setReferralError("");
    } catch {
      setReferralError("Referral lookup failed.");
    }
  }

  async function handlePayNow(selectedPaymentMethod, paymentDetails = {}, agentAuth = {}) {
    if (!paymentModal) return;
    setPayProcessing(true);
    await new Promise((r) => setTimeout(r, 1800));

    const isFirstPayment = !hasPaid;
    const protectFee = isFirstPayment ? PROTECT_FEE_FIRST : PROTECT_FEE_REPEAT;
    const app = { ...paymentModal, protectFee,
      totalFee: (paymentModal.govFee || 0) + (paymentModal.svcFee || 0) + protectFee + (paymentModal.expressFee || 0),
    };
    setPaymentModal(app);

    const svcFee = app.svcFee || 0;
    let finalSvc = Math.max(0, (svcFee * applicantList.length) + protectFee - discountAmount);
    let walletOfferApplied = 0;

    if (useWalletOffer) {
      const currentWallet = wallet;
      const nonReBal = currentWallet.nonRedeemable?.balance || 0;
      if (nonReBal > 0 && finalSvc >= MIN_SVC_FEE_REDEMPTION) {
        walletOfferApplied = Math.min(nonReBal, WELCOME_BONUS);
        finalSvc = Math.max(0, finalSvc - walletOfferApplied);
        await redeemNonRedeemablePoints(user?.email, walletOfferApplied, app.countryName);
      }
    }

    if (referralApplied && referralInput.trim()) {
      const code = referralInput.trim().toUpperCase();
      try {
        await lookupReferralOwner(code);
      } catch { /* ignore */ }
    }

    setHasPaid(true);
    const currentRole = user?.role || "individual";

    if (isFirstPayment && currentRole !== "individual") {
      const code = await assignReferralCode(user?.email, user?.userId, currentRole);
      if (code) setReferralCode(code);
    }

    const updatedMilestoneData = await processMilestoneRewards(user?.email, currentRole, commissionableBase(svcFee * applicantList.length), paymentModal?.country || "");
    setMilestoneData({ ...updatedMilestoneData });

    _clearDraft(app.applicationId);
    setDraftMap((prev) => { const m = { ...prev }; delete m[app.applicationId]; return m; });

    const payModalAppId = paymentModal.applicationId || paymentModal.id || paymentModal.human_id;

    try {
      const result = await ApplicationService.create({
        visaSlug: paymentModal.visaSlug || paymentModal.visa_slug,
        isExpress: paymentModal.isExpress || paymentModal.is_express || false,
      });

      // Persist payment status on the backend so it survives refresh
      try {
        const payResult = await ApplicationService.pay(result.id);
        Object.assign(result, payResult);
      } catch (payErr) {
        console.error("[API] Failed to mark as paid:", payErr);
      }

      const paidApp = {
        ...(result || {}),
        ...paymentModal,
        applicants: applicantList,
        status: APPLICATION_STATUS.COMPLETED,
      };

      setApplications((prev) => {
        const filtered = prev.filter(
          (a) => a.applicationId !== payModalAppId && a.id !== payModalAppId && a.human_id !== payModalAppId
        );
        return [paidApp, ...filtered];
      });

    } catch (err) {
      console.error("[API] Failed to save application:", err);
      setApplications((prev) => prev.map((a) =>
        (a.applicationId === payModalAppId || a.id === payModalAppId || a.human_id === payModalAppId)
          ? { ...a, status: APPLICATION_STATUS.COMPLETED }
          : a
      ));
    }

    const freshWallet = await getWallet(user?.email);
    setWallet(freshWallet);
    setPayProcessing(false);
    closePaymentModal();
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  }

  function handleDeleteApplication(applicationId) {
    if (!window.confirm("Delete this application? This cannot be undone.")) return;
    deleteApplication(user.email, applicationId).then(() => {
      setApplications((prev) => prev.filter(
        (a) => a.applicationId !== applicationId && a.id !== applicationId && a.human_id !== applicationId
      ));
    });
  }

  async function handleRedeemPoints(amount, country) {
    const result = await redeemNonRedeemablePoints(user.email, amount, country);
    if (result.success) {
      setWallet({ ...result.wallet });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2500);
    }
    return result.success;
  }

  async function handleSubmitBillRequest(formData) {
    try {
      const apiResult = await RedemptionService.submitRedemption(
        parseFloat(formData.amount) || 0,
        formData.billFile
      );
      sendAdminEmail("New Bill Redemption Request — TheVisaCo", JSON.stringify({
        id: apiResult.human_id || apiResult.id,
        offerName: formData.offerName,
        category: formData.category,
        amount: parseFloat(formData.amount) || 0,
      }));
      const list = await fetchRedemptionRequests(user.email);
      setRedemptionRequests(list);
      return { id: apiResult.human_id || apiResult.id };
    } catch {
      sendAdminEmail("New Bill Redemption Request — TheVisaCo", JSON.stringify(formData));
      const list = await fetchRedemptionRequests(user.email);
      setRedemptionRequests(list);
      return { id: `bill_${Date.now()}` };
    }
  }

  async function handleVoucherRedemptionRequest(voucher) {
    try {
      await RedemptionService.redeemVoucher(voucher.id);
      sendAdminEmail("Voucher Redemption Request — TheVisaCo", JSON.stringify(voucher));
    } catch {
      sendAdminEmail("Voucher Redemption Request — TheVisaCo (fallback)", JSON.stringify(voucher));
    }
    const list = await fetchVoucherRedemptions(user.email);
    setVoucherRedemptions(list);
    return { id: voucher.id, voucherId: voucher.id, status: REDEMPTION_STATUS.PENDING };
  }

  async function handlePayoutRequest(formData) {
    if (formData._updateList) { setPayoutRequests([...formData._updateList]); return; }
    try {
      const apiResult = await RedemptionService.requestPayout(
        parseFloat(formData.amount) || 0,
        formData.chequeFile
      );
      sendAdminEmail("Payout Request — TheVisaCo", JSON.stringify(formData));
      const list = await fetchPayoutRequests(user.email);
      setPayoutRequests(list);
      return { id: apiResult.human_id || apiResult.id, amount: parseFloat(formData.amount) || 0 };
    } catch {
      sendAdminEmail("Payout Request — TheVisaCo (fallback)", JSON.stringify(formData));
      const list = await fetchPayoutRequests(user.email);
      setPayoutRequests(list);
      return { id: `payout_${Date.now()}`, amount: parseFloat(formData.amount) || 0 };
    }
  }

  function handleLogout() {
    authLogout();
    if (onLogout) onLogout();
  }

  function handleSetDecision(applicationId, decision) {
    setApplications((prev) => prev.map((a) =>
      (a.applicationId === applicationId || a.id === applicationId || a.human_id === applicationId)
        ? { ...a, decisionStatus: decision, updatedAt: new Date().toISOString() }
        : a
    ));
  }

  return {
    user, setUser, showWelcome, dismissWelcome,
    editMode, setEditMode, activeTab, setActiveTab,
    uploading, saveSuccess, setSaveSuccess,
    cameraActive, videoRef, cameraDocKey,
    docFiles, uploadedDocs,
    editName, setEditName, editPhone, setEditPhone, editCity, setEditCity,
    docRequirements, roleConfig, commissionRate, commissionPct,
    handleFilePick, openCamera, capturePhoto, closeCamera,
    handleUploadDocuments, handleSaveProfile, handleLogout,
    applications, pendingApplications, completedApplications,
    processingApplications, decisionApplications,
    handleSetDecision, handleDeleteApplication,
    wallet, nonRedeemableBalance: wallet?.nonRedeemable?.balance ?? 0,
    handleRedeemPoints, referralCode, milestoneData,
    paymentModal, openPaymentModal, closePaymentModal, saveCurrentDraft,
    payStep, setPayStep,
    applicantList, updateApplicantField, handleApplicantFilePick, removeApplicant, addApplicant,
    appCountInput, setAppCountInput, handleApplicationCountChange,
    confirmProceedWithCount,
    showUpgradeModal, handleUpgradeDecision,
    referralInput, setReferralInput, referralError, setReferralError,
    referralApplied, discountAmount, applyReferralCode,
    useWalletOffer, setUseWalletOffer, walletOfferError, setWalletOfferError,
    selectedPaymentMethod, setSelectedPaymentMethod,
    payProcessing, handlePayNow,
    APPLICATION_STATUS, DECISION_STATUS_LABELS, TAB_LABELS,
    MAX_APPLICATIONS, INDIVIDUAL_LIMIT,
    redemptionRequests, setRedemptionRequests,
    voucherRedemptions, setVoucherRedemptions,
    payoutRequests, setPayoutRequests,
    handleSubmitBillRequest, handleVoucherRedemptionRequest,
    handlePayoutRequest, REDEMPTION_STATUS, draftMap,
    hasPaid,
  };
}

function _defaultWallet() {
  return {
    nonRedeemable: { balance: 0, transactions: [] },
    redeemable: { balance: 0, transactions: [] },
  };
}

// ════════════════════════════════════════════════
//  NAV ITEMS
// ════════════════════════════════════════════════
export const NAV_ITEMS_META = [
  { key: "overview",     label: "Overview"     },
  { key: "applications", label: "Applications" },
  { key: "rewards",      label: "Rewards"      },
  { key: "documents",    label: "Documents"    },
  { key: "settings",     label: "Settings"     },
];

export const SIDEBAR_LINKS = [
  { key: "eSIMs",      label: "eSIMs",         icon: "e",  path: "/Esim"      },
  { key: "interview", label: "AI Interview", icon: "🎙️", path: "/interview" },
];

export const TAB_TITLES = {
  overview:     "Dashboard",
  applications: "My Applications",
  rewards:      "Rewards & Wallet",
  documents:    "My Documents",
  settings:     "Settings",
};

export const TAB_SUBS = {
  overview:     null,
  applications: "Track and manage your visa applications",
  rewards:      "Your wallet, commissions, vouchers & referral code",
  documents:    "Upload and manage your verification documents",
  settings:     "Manage your account preferences",
};

// ════════════════════════════════════════════════
//  PAYMENT METHODS & CONVENIENCE CHARGES
// ════════════════════════════════════════════════

export const PAYMENT_METHOD_KEYS = {
  DEBIT_VISA: "debit_visa", DEBIT_MASTERCARD: "debit_mastercard", DEBIT_RUPAY: "debit_rupay",
  CREDIT_VISA: "credit_visa", CREDIT_MASTERCARD: "credit_mastercard", CREDIT_RUPAY: "credit_rupay",
  NETBANKING_NEFT: "netbanking_neft", NETBANKING_IMPS: "netbanking_imps", NETBANKING_RTGS: "netbanking_rtgs",
  UPI: "upi",
};

export const PAYMENT_METHODS = {
  [PAYMENT_METHOD_KEYS.DEBIT_VISA]: { key: PAYMENT_METHOD_KEYS.DEBIT_VISA, category: "debit", network: "Visa", label: "Debit Card — Visa", shortLabel: "Visa Debit", icon: "visa", description: "Widely accepted debit card for online payments", conveniencePct: 0.009, convenienceFlat: 0, minCharge: 5, maxCharge: null, taxPct: 0.18, processingTime: "Instant" },
  [PAYMENT_METHOD_KEYS.DEBIT_MASTERCARD]: { key: PAYMENT_METHOD_KEYS.DEBIT_MASTERCARD, category: "debit", network: "Mastercard", label: "Debit Card — Mastercard", shortLabel: "Mastercard Debit", icon: "mastercard", description: "Globally accepted debit card", conveniencePct: 0.009, convenienceFlat: 0, minCharge: 5, maxCharge: null, taxPct: 0.18, processingTime: "Instant" },
  [PAYMENT_METHOD_KEYS.DEBIT_RUPAY]: { key: PAYMENT_METHOD_KEYS.DEBIT_RUPAY, category: "debit", network: "RuPay", label: "Debit Card — RuPay", shortLabel: "RuPay Debit", icon: "rupay", description: "Indian domestic debit network — zero convenience fee", conveniencePct: 0, convenienceFlat: 0, minCharge: 0, maxCharge: 0, taxPct: 0.18, processingTime: "Instant" },
  [PAYMENT_METHOD_KEYS.CREDIT_VISA]: { key: PAYMENT_METHOD_KEYS.CREDIT_VISA, category: "credit", network: "Visa", label: "Credit Card — Visa", shortLabel: "Visa Credit", icon: "visa", description: "Earn rewards while paying for your visa fees", conveniencePct: 0.015, convenienceFlat: 0, minCharge: 10, maxCharge: null, taxPct: 0.18, processingTime: "Instant" },
  [PAYMENT_METHOD_KEYS.CREDIT_MASTERCARD]: { key: PAYMENT_METHOD_KEYS.CREDIT_MASTERCARD, category: "credit", network: "Mastercard", label: "Credit Card — Mastercard", shortLabel: "Mastercard Credit", icon: "mastercard", description: "Widely used credit card with reward points", conveniencePct: 0.015, convenienceFlat: 0, minCharge: 10, maxCharge: null, taxPct: 0.18, processingTime: "Instant" },
  [PAYMENT_METHOD_KEYS.CREDIT_RUPAY]: { key: PAYMENT_METHOD_KEYS.CREDIT_RUPAY, category: "credit", network: "RuPay", label: "Credit Card — RuPay", shortLabel: "RuPay Credit", icon: "rupay", description: "Made in India credit card — low convenience fee", conveniencePct: 0.005, convenienceFlat: 0, minCharge: 5, maxCharge: null, taxPct: 0.18, processingTime: "Instant" },
  [PAYMENT_METHOD_KEYS.NETBANKING_NEFT]: { key: PAYMENT_METHOD_KEYS.NETBANKING_NEFT, category: "netbanking", network: "NEFT", label: "Net Banking — NEFT", shortLabel: "NEFT", icon: "bank", description: "National Electronic Funds Transfer — batch processing", conveniencePct: 0, convenienceFlat: 5, minCharge: 5, maxCharge: 5, taxPct: 0.18, processingTime: "2–4 hours (batch)", note: "NEFT processes in batches. Confirmation may take up to 4 hours on working days." },
  [PAYMENT_METHOD_KEYS.NETBANKING_IMPS]: { key: PAYMENT_METHOD_KEYS.NETBANKING_IMPS, category: "netbanking", network: "IMPS", label: "Net Banking — IMPS", shortLabel: "IMPS", icon: "bank", description: "Immediate Payment Service — instant 24×7 transfer", conveniencePct: 0, convenienceFlat: 10, minCharge: 10, maxCharge: 10, taxPct: 0.18, processingTime: "Instant (24×7)", note: "IMPS is available round the clock including weekends and bank holidays." },
  [PAYMENT_METHOD_KEYS.NETBANKING_RTGS]: { key: PAYMENT_METHOD_KEYS.NETBANKING_RTGS, category: "netbanking", network: "RTGS", label: "Net Banking — RTGS", shortLabel: "RTGS", icon: "bank", description: "Real Time Gross Settlement — recommended for large amounts (≥ ₹2 L)", conveniencePct: 0, convenienceFlat: 25, minCharge: 25, maxCharge: 25, taxPct: 0.18, processingTime: "Real-time (banking hours)", minAmount: 200000, note: "RTGS is recommended for payments ₹2,00,000 and above. Real-time settlement during banking hours." },
  [PAYMENT_METHOD_KEYS.UPI]: { key: PAYMENT_METHOD_KEYS.UPI, category: "upi", network: "UPI", label: "UPI / Google Pay / PhonePe / Paytm", shortLabel: "UPI", icon: "upi", description: "Instant payments via UPI apps — zero convenience fee", conveniencePct: 0, convenienceFlat: 0, minCharge: 0, maxCharge: 0, taxPct: 0.18, processingTime: "Instant" },
};

export const PAYMENT_METHOD_GROUPS = [
  { groupKey: "debit", groupLabel: "Debit Cards", icon: "card", description: "Directly debited from your bank account", methods: [PAYMENT_METHODS[PAYMENT_METHOD_KEYS.DEBIT_VISA], PAYMENT_METHODS[PAYMENT_METHOD_KEYS.DEBIT_MASTERCARD], PAYMENT_METHODS[PAYMENT_METHOD_KEYS.DEBIT_RUPAY]] },
  { groupKey: "credit", groupLabel: "Credit Cards", icon: "card", description: "Pay now, settle later — earn rewards", methods: [PAYMENT_METHODS[PAYMENT_METHOD_KEYS.CREDIT_VISA], PAYMENT_METHODS[PAYMENT_METHOD_KEYS.CREDIT_MASTERCARD], PAYMENT_METHODS[PAYMENT_METHOD_KEYS.CREDIT_RUPAY]] },
  { groupKey: "netbanking", groupLabel: "Net Banking", icon: "bank", description: "Direct payment from your bank account", methods: [PAYMENT_METHODS[PAYMENT_METHOD_KEYS.NETBANKING_NEFT], PAYMENT_METHODS[PAYMENT_METHOD_KEYS.NETBANKING_IMPS], PAYMENT_METHODS[PAYMENT_METHOD_KEYS.NETBANKING_RTGS]] },
  { groupKey: "upi", groupLabel: "UPI / Wallet", icon: "mobile", description: "Pay instantly via UPI apps", methods: [PAYMENT_METHODS[PAYMENT_METHOD_KEYS.UPI]] },
];

export const DEFAULT_PAYMENT_METHOD = PAYMENT_METHOD_KEYS.DEBIT_RUPAY;

export function calculateConvenienceFee(amount, methodKey) {
  const method = PAYMENT_METHODS[methodKey];
  if (!method) return { baseFee: 0, gst: 0, totalFee: 0, grandTotal: amount };
  let baseFee = (amount * method.conveniencePct) + method.convenienceFlat;
  if (method.minCharge > 0) baseFee = Math.max(baseFee, method.minCharge);
  if (method.maxCharge !== null && method.maxCharge >= 0) baseFee = Math.min(baseFee, method.maxCharge);
  baseFee = Math.round(baseFee);
  const gst = Math.round(baseFee * method.taxPct);
  return { baseFee, gst, totalFee: baseFee + gst, grandTotal: amount + baseFee + gst };
}

export const APP_DOC_LABELS = {
  passportFront: "Passport – First Page", passportBack: "Passport – Last Page",
  bankStatement: "Bank Statement", visaPhoto: "Photo for Visa",
  biometric: "Biometric Receipt", flightTicket: "Flight Ticket",
  travelInsurance: "Travel Insurance", invitationLetter: "Invitation Letter", otherDoc: "Other Document", otherDoc2: "Other Document 2",
};

export const COUNTRY_REGIONS = {
  Thailand: "Asia", Japan: "Asia", Singapore: "Asia", Vietnam: "Asia",
  Indonesia: "Asia", Malaysia: "Asia", Philippines: "Asia", "Sri Lanka": "Asia",
  Nepal: "Asia", Maldives: "Asia", Bali: "Asia", China: "Asia", "South Korea": "Asia",
  Dubai: "Middle East", UAE: "Middle East", "Saudi Arabia": "Middle East",
  Qatar: "Middle East", Bahrain: "Middle East", Oman: "Middle East", Kuwait: "Middle East",
  Jordan: "Middle East", Turkey: "Middle East",
  France: "Europe", UK: "Europe", Germany: "Europe", Italy: "Europe",
  Spain: "Europe", Netherlands: "Europe", Switzerland: "Europe", Portugal: "Europe",
  Greece: "Europe", Austria: "Europe", Belgium: "Europe", Sweden: "Europe",
  Czech: "Europe", Poland: "Europe", Hungary: "Europe",
  USA: "Americas", Canada: "Americas", Brazil: "Americas", Mexico: "Americas",
  Australia: "Pacific", "New Zealand": "Pacific",
  Kenya: "Africa", Egypt: "Africa", "South Africa": "Africa", Morocco: "Africa",
};

export const DOC_ALL_REGIONS = ["All", "Asia", "Middle East", "Europe", "Americas", "Pacific", "Africa", "Other"];

export function getDocRegion(country) {
  if (!country) return "Other";
  for (const [k, v] of Object.entries(COUNTRY_REGIONS)) {
    if (country.toLowerCase().includes(k.toLowerCase())) return v;
  }
  return "Other";
}

export const REGION_MAP = {
  Asia: ["Thailand","Japan","Singapore","Indonesia","Vietnam","Malaysia","Philippines","India","China","South Korea","Sri Lanka","Nepal","Bangladesh","Myanmar","Cambodia","Laos","Taiwan","Hong Kong","Maldives","Bhutan"],
  Europe: ["France","Germany","Italy","Spain","United Kingdom","Portugal","Netherlands","Greece","Austria","Switzerland","Belgium","Sweden","Norway","Denmark","Finland","Poland","Czech Republic","Hungary","Romania","Croatia","Turkey","Ireland","Luxembourg","Slovakia","Slovenia","Estonia","Latvia","Lithuania","Malta","Cyprus","Bulgaria","Serbia","Montenegro","Albania","Iceland"],
  Americas: ["United States","Canada","Mexico","Brazil","Argentina","Chile","Colombia","Peru","Ecuador","Bolivia","Venezuela","Uruguay","Paraguay","Cuba","Jamaica","Costa Rica","Panama","Dominican Republic","Trinidad and Tobago","Barbados"],
  "Middle East": ["UAE","Saudi Arabia","Qatar","Kuwait","Bahrain","Oman","Jordan","Lebanon","Egypt","Israel","Iran","Iraq","Syria","Yemen"],
  Africa: ["South Africa","Kenya","Nigeria","Ethiopia","Tanzania","Ghana","Morocco","Tunisia","Egypt","Rwanda","Uganda","Zimbabwe","Mozambique","Senegal","Cameroon","Côte d'Ivoire"],
  Oceania: ["Australia","New Zealand","Fiji","Papua New Guinea","Samoa","Vanuatu","Solomon Islands","Tonga"],
};

export const APP_ALL_REGIONS = ["All", ...Object.keys(REGION_MAP)];

export function getAppRegion(country) {
  if (!country) return "Other";
  for (const [region, countries] of Object.entries(REGION_MAP)) {
    if (countries.some((c) => c.toLowerCase() === country.toLowerCase())) return region;
  }
  return "Other";
}

// ════════════════════════════════════════════════════════════
//  CONSENT FORM CONSTANTS
// ════════════════════════════════════════════════════════════
export const CONSENT_CLAUSES = [
  { id: 1, title: "1. Accuracy of Information :", body: "I declare that all information provided by me and on behalf of other applicants is true, complete, and accurate to the best of my knowledge. I understand that any false, misleading, or incomplete information may result in rejection of the application without refund." },
  { id: 2, title: "2. Authenticity of Documents :", body: "I confirm that all documents uploaded by me (including identity proofs, passports, photographs, and supporting documents) are genuine, valid, and belong to the respective applicant. I understand that submission of forged or altered documents may lead to legal action and permanent application denial." },
  { id: 3, title: "Authorization to Act :", body: "I authorize the platform and its representatives to assist me in the visa application process, including reviewing, submitting, and managing my application with relevant authorities on my behalf." },
  { id: 4, title: "4. Third-Party Applicant Details :", body: "If I am submitting details for other applicants, I confirm that I have obtained their consent to share their personal information and documents for visa processing purposes." },
  { id: 5, title: "5. Data Usage & Privacy :", body: "I consent to the collection, storage, and processing of my personal data and documents for the purpose of visa application, verification, and related services, in accordance with the platform's privacy policy." },
  { id: 6, title: "6. Refund Policy :", body: "I understand and agree that any refund requests will be processed strictly in accordance with the platform's refund policy. I acknowledge that not all cases are eligible for refunds, especially after submission to authorities or initiation of processing." },
  { id: 7, title: "7. Service Scope :", body: "I understand that the platform provides assistance and facilitation services only and does not guarantee visa approval, as decisions are solely made by the respective embassy or immigration authorities." },
  { id: 8, title: "8. Real-Time Assistance (If Applicable) :", body: "I acknowledge that I may receive real-time assistance from an agent during the application process, and I consent to such support for completing my application." },
];

export const CONSENT_DECLARATION_TEXT = "I have read, understood, and agree to all the terms and conditions stated above.";

export function checkRequiredDocsComplete(userEmail, role) {
  if (!userEmail) return { complete: false, missing: [] };
  const reqs = DOCUMENT_REQUIREMENTS[role] || DOCUMENT_REQUIREMENTS.individual;
  return { complete: true, missing: [] };
}

